import { IFeatures, IRuntimeAppSettings } from '@model';
import { EPage } from '@types';
import {
  BENEFIT_FEATURE_SWITCH,
  DUAL_PRICE_ORDER_FEATURE_SWITCH,
  THANK_YOU,
  GOOGLE_RETAIL_API_USER_EVENTS,
  UPSELLING_REDESIGN_V2,
  DELIVERY_AND_PAYMENT_FEATURE_SWITCH,
  ENABLE_LOWER_ZOOM,
  ENABLE_LOYALTY,
  ENABLE_STICKY_BUTTONS_CART,
  ENABLE_STICKY_BUTTONS_DAP,
  ENABLE_STICKY_BUTTONS_UPSELLING,
  DISABLE_SALE_PRICE,
  ENABLE_PRZELEWY_PAYPO_NEW_FLOW_OALT,
  ENABLE_PLUSMINUS_BUTTONS_CART,
} from '@utils/constants';
import { firstOrDefault } from '@utils/math';

import { IAggregatedSettingsResponse, ICheckoutAppSettingResponse, IHeaderAppSettingsResponse } from './model';

export const transformAppConfiguration = (settings: IAggregatedSettingsResponse): IRuntimeAppSettings => ({
  ...transformCheckoutAppSettings(settings.checkoutConfiguration.data),
  ...transformHeaderAppSettings(settings.headerConfiguration.data),
});

const transformCheckoutAppSettings = (settings: ICheckoutAppSettingResponse) => ({
  localization: {
    languageIdentifier: settings.languageIdentifier,
    countryIdentifier: settings.countryIdentifier,
  },
  shopSettings: {
    orderLimit: settings.orderLimit,
  },
  features: transformFeatures(settings.features),
  primaryCurrency: settings.primaryCurrency,
  secondaryCurrency: settings?.secondaryCurrency,
  companyName: settings.companyName,
});

const transformHeaderAppSettings = ({
  quickContact: { isCallCenterOpen, workingHours, phoneNumbers },
}: IHeaderAppSettingsResponse) => ({
  callCenter: {
    openingHours: workingHours,
    number: firstOrDefault<string>(phoneNumbers, ''),
    isOpened: isCallCenterOpen,
  },
});

const transformFeatures = (features: string[]): IFeatures => {
  const featureSet = new Set(features);
  // prepare pages
  const pages = new Set([EPage.CART, EPage.UPSELLING, EPage.ADYEN_PAYMENT_REDIRECT_PAGE]);
  if (featureSet.has(THANK_YOU)) {
    pages.add(EPage.THANK_YOU);
  }

  if (featureSet.has(DELIVERY_AND_PAYMENT_FEATURE_SWITCH)) {
    pages.add(EPage.DELIVERY_AND_PAYMENT);
  }

  return {
    benefits: featureSet.has(BENEFIT_FEATURE_SWITCH),
    pages,
    isPrimaryCurrencyFirst: featureSet.has(DUAL_PRICE_ORDER_FEATURE_SWITCH),
    googleRetailApiUserEvents: featureSet.has(GOOGLE_RETAIL_API_USER_EVENTS),
    upsellingRedesign: featureSet.has(UPSELLING_REDESIGN_V2),
    enableLowerZoom: featureSet.has(ENABLE_LOWER_ZOOM),
    enableLoyalty: featureSet.has(ENABLE_LOYALTY),
    enableStickyButtonsOnCart: featureSet.has(ENABLE_STICKY_BUTTONS_CART),
    enableStickyButtonsOnDap: featureSet.has(ENABLE_STICKY_BUTTONS_DAP),
    enableStickyButtonsOnUpselling: featureSet.has(ENABLE_STICKY_BUTTONS_UPSELLING),
    disableSalePrice: featureSet.has(DISABLE_SALE_PRICE),
    enablePrzelewyPaypoNewFlowOalt: featureSet.has(ENABLE_PRZELEWY_PAYPO_NEW_FLOW_OALT),
    enablePlusMinusButtonsOnCart: featureSet.has(ENABLE_PLUSMINUS_BUTTONS_CART),
  };
};
